import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, TrendingDown, ShoppingCart, IndianRupee, Clock, AlertTriangle, Package, Truck, Users, FileText, RefreshCw, Calendar, ClipboardCheck, ArrowRight, Search, X, Download, Wallet, CreditCard, Minus } from "lucide-react";
import { MultiSelectDropdown } from "@/components/ui/multi-select-dropdown";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";

const COLORS = ["#09090B", "#2563EB", "#16A34A", "#EA580C", "#7C3AED", "#EC4899", "#F59E0B", "#06B6D4"];
const PIPELINE_COLORS = {
  "Draft": "#94A3B8", "Pending with Manager": "#7C3AED", "Pending with Admin": "#D97706",
  "Pending with Finance": "#2563EB", "At Warehouse": "#EA580C", "Under Processing": "#C2410C",
  "Ready for Dispatch": "#CA8A04", "Vehicle Assigned": "#0891B2", "Invoice Uploaded": "#4F46E5",
  "Dispatched": "#0D9488", "Delivered": "#16A34A", "Closure Pending": "#DB2777", "Order Closed": "#15803D",
};
const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);
const fmtR = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const paise_to_rupees = (p) => (p || 0) / 100;


// FINANCE BATCH 4 (4.4d) — one plain sentence per widget. Keyed by the widget's test id or title.
export const WIDGET_INFO = {
  "kpi-orders": "Orders punched this period, whether invoiced or not — not revenue. The booked value is the order value excl GST, by order date, without cancelled and rejected orders.",
  "Top Customers": "Invoiced revenue per customer — excl GST, by invoice date, the same revenue the Revenue card and the PnL report show.",
  "kpi-revenue": "Invoiced sales excluding GST for the period, by invoice date; cancelled and rejected orders excluded. Includes orders not yet GRN'd — see PnL Audit for the split.",
  "kpi-outstanding": "Money still to come in on invoiced orders, all time, from the receivables engine; cancelled, rejected and not-yet-invoiced orders are not counted.",
  "kpi-pending": "Orders sitting at Pending with Manager, Pending with Admin or Pending with Finance right now.",
  "kpi-warehouse": "Orders at At Warehouse or Under Processing right now.",
  "kpi-packed": "Orders that moved to Ready for Dispatch today (India time).",
  "kpi-dispatched": "Orders that moved to Dispatched or Shipped today (India time).",
  "kpi-overdue": "Orders past their stored due date with more than Rs 1 outstanding, from the one due-date engine; closed, cancelled and pending orders are never counted.",
  "kpi-customers": "Customers who placed at least one order in the chosen period, by order date.",
  "kpi-inactive": "Customers with no order in the last 30 days.",
  "kpi-close": "Delivered orders with nothing left outstanding, waiting for closure.",
  "kpi-awaiting-vehicle": "Orders at the warehouse waiting for a vehicle to be assigned.",
  "kpi-in-transit": "Orders currently at Dispatched.",
  "kpi-receivables": "Net outstanding on invoiced orders, from the receivables report; the cards are all-time.",
  "kpi-received": "Payments received in the chosen period, by payment date.",
  "kpi-cn-settled": "Invoice value settled with credit notes instead of cash.",
  "kpi-deductions": "TDS, claims and short payments recorded against invoices.",
  "kpi-overdue-recv": "Overdue amount on invoiced orders, past the stored due date, from the one due-date engine.",
  "kpi-cost-per-kg": "Actual freight divided by chargeable weight for dispatched orders in the period; it falls back to estimated freight until the vendor bill is uploaded.",
  "kpi-cost-pct": "Freight as a percentage of invoiced sales excluding GST for the very same orders — the same revenue rule the P&L uses. Marked (est.) while any freight in it is still an estimate.",
  "kpi-accuracy": "How close the estimate was to the vendor bill, on orders that have both.",
  "Revenue by Channel (MTD)": "Invoiced sales excluding GST per sales channel for the period, by invoice date; cancelled and rejected orders excluded. Includes orders not yet GRN'd \u2014 see PnL Audit for the split.",
  "Order Pipeline": "How many orders sit at each status right now.",
  "Top Customers by Revenue": "Customers ranked by invoiced value in the chosen period.",
  "Receivables Ageing": "Open invoiced orders bucketed by how many days past their stored due date they are (Not due, 1-15, 16-30, 31-60, 61-90, 90+).",
  "Pending Approvals": "Orders waiting for the approval you can give, oldest first.",
  "Top SKUs": "SKUs ranked by quantity or value for the chosen period, by order date.",
  "Team Performance": "Two figures per sales person: Booked is the order value incl GST by order date; Invoiced revenue is excl GST by invoice date, the same revenue the Revenue card shows. Orders with no owner are shown as Unassigned.",
  "Overdue Payments": "The oldest overdue orders by stored due date, with the days overdue and the amount still to come.",
  "Follow-Up — Inactive Customers": "Customers with no order in the last 30 days, for a follow-up call.",
  "Warehouse Queue": "Orders waiting at the warehouse, oldest first.",
  "PnL Snapshot (Invoice Date Basis)": "Revenue, frozen COGS, freight and margin for the period, by invoice date; cancelled and rejected orders excluded. Includes orders not yet GRN'd — see PnL Audit for the split.",
  "Staff Productivity (Today)": "What each user did today (India time).",
  "Expiry Alerts (30 Days)": "Stock batches expiring within 30 days.",
  "Freight Cost MTD": "Freight on orders dispatched this month versus last month, read the same way PnL reads it (actual, else estimated).",
  "Transporter Scorecard (MTD)": "Shipments, freight and on-time delivery per transporter this month.",
  "SS Stock Status": "Current stock position per SKU at the super stockist.",
  "Dispatched Orders — Actual Dispatch Dates": "Orders dispatched in the chosen period with the date the vehicle actually left.",
};

function InfoDot({ text, testId }) {
  if (!text) return null;
  return (
    <span title={text} data-testid={testId}
          className="inline-flex items-center justify-center w-3.5 h-3.5 ml-1 rounded-full border border-zinc-300 text-[8px] text-zinc-500 cursor-help align-middle">
      i
    </span>
  );
}

function KPI({ icon: Icon, label, value, sub, change, color, onClick, testId, inverse = false, changeLabel = "vs last period" }) {
  return (
    <button onClick={onClick} className="kpi-tile text-left w-full" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-[10px] font-semibold tracking-[0.06em] uppercase text-zinc-400">{label}<InfoDot text={WIDGET_INFO[testId]} testId={`${testId}-info`} /></p>
          <p className={`font-heading text-2xl font-bold mt-1 ${color || 'text-zinc-900'}`}>{value}</p>
          {sub && <p className="text-[10px] text-zinc-400 mt-0.5">{sub}</p>}
          {change !== undefined && change !== null && change !== 0 && (
            <div className={`flex items-center gap-0.5 mt-1 text-[10px] font-semibold ${inverse ? (change > 0 ? 'text-red-600' : 'text-green-600') : (change > 0 ? 'text-green-600' : 'text-red-600')}`}>
              {change > 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {Math.abs(change)}% {changeLabel}
            </div>
          )}
          {change === undefined && inverse && <p className="text-[10px] text-zinc-300 mt-1">–</p>}
        </div>
        <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-sm shrink-0"><Icon size={16} strokeWidth={1.5} className="text-zinc-600" /></div>
      </div>
    </button>
  );
}

function WidgetCard({ title, children, className = "", info = "" }) {
  const key = typeof title === "string" ? title : "";
  const text = info || WIDGET_INFO[key] || "";
  return (
    <div className={`border border-zinc-200 ${className}`}>
      <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-zinc-900">{title}<InfoDot text={text} testId={`widget-info-${(key || info).slice(0, 24).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`} /></h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getValues } = useConfig(["channel_types"]);
  const roles = user?.roles || [];
  const permissions = user?.permissions || [];
  const isAdmin = roles.some(r => ["Admin", "admin"].includes(r)) || permissions.includes("admin.full_access");
  const isSalesMgr = roles.some(r => r === "Sales Manager");
  const isSalesExec = roles.some(r => r === "Sales Executive");
  const isFinance = roles.some(r => ["Finance Manager", "Finance Executive", "Finance — Invoicing Executive"].includes(r));
  const isFinMgr = roles.some(r => r === "Finance Manager");
  const isWarehouse = roles.some(r => ["Warehouse Manager", "Warehouse Executive"].includes(r));
  const isWhMgr = roles.some(r => r === "Warehouse Manager");
  const isLogistics = roles.some(r => ["Logistics Manager", "Logistics Coordinator", "Logistics Executive"].includes(r));
  const isLogMgr = roles.some(r => r === "Logistics Manager");

  // Global date range (controls all widgets)
  const today = new Date().toISOString().split('T')[0];
  const monthStart = today.slice(0, 8) + "01";
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [datePreset, setDatePreset] = useState("this_month");

  const applyPreset = (preset) => {
    const now = new Date();
    setDatePreset(preset);
    if (preset === "today") { setDateFrom(today); setDateTo(today); }
    else if (preset === "this_week") { const d = new Date(now); d.setDate(d.getDate() - d.getDay()); setDateFrom(d.toISOString().split('T')[0]); setDateTo(today); }
    else if (preset === "this_month") { setDateFrom(today.slice(0, 8) + "01"); setDateTo(today); }
    else if (preset === "last_month") { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); setDateFrom(d.toISOString().split('T')[0]); setDateTo(e.toISOString().split('T')[0]); }
    else if (preset === "this_quarter") { const q = Math.floor(now.getMonth() / 3) * 3; setDateFrom(`${now.getFullYear()}-${String(q + 1).padStart(2, '0')}-01`); setDateTo(today); }
    else if (preset === "this_year") { setDateFrom(`${now.getFullYear()}-01-01`); setDateTo(today); }
  };

  const [stats, setStats] = useState(null);
  const [channelData, setChannelData] = useState([]);
  const [pipelineData, setPipelineData] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topSKUs, setTopSKUs] = useState([]);
  const [topSKUsByQty, setTopSKUsByQty] = useState([]);
  const [topSKUsPrev, setTopSKUsPrev] = useState([]);
  const [skuView, setSkuView] = useState("revenue"); // "revenue" | "quantity"
  const [ageingData, setAgeingData] = useState([]);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [overdueOrders, setOverdueOrders] = useState([]);
  const [teamPerf, setTeamPerf] = useState([]);
  const [inactiveCustomers, setInactiveCustomers] = useState([]);
  const [warehouseQueue, setWarehouseQueue] = useState([]);
  const [staffProductivity, setStaffProductivity] = useState([]);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [freightCostMTD, setFreightCostMTD] = useState(null);
  const [transporterScorecard, setTransporterScorecard] = useState([]);
  const [ssStockWidget, setSsStockWidget] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Dispatch widget state
  const [dispatchWidget, setDispatchWidget] = useState({ data: [], total: 0, summary: {} });
  const [dispatchPage, setDispatchPage] = useState(1);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const canViewDispatch = permissions.includes("dashboard.dispatch_widget_view") || isAdmin || isLogistics || isSalesMgr || isSalesExec;
  const canEditDispatch = permissions.includes("dashboard.dispatch_widget_edit") || isAdmin || isLogistics;
  const canDeleteDispatch = permissions.includes("dashboard.dispatch_widget_delete") || isAdmin || isLogistics;
  const canExportDispatch = canEditDispatch; // Tier 1 only
  const [dispatchSearch, setDispatchSearch] = useState("");
  const [dispatchChannelFilter, setDispatchChannelFilter] = useState([]);
  const [dispatchTFFilter, setDispatchTFFilter] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [mySlaScore, setMySlaScore] = useState(null);
  const [logKpis, setLogKpis] = useState(null);
  const [grnWaiting, setGrnWaiting] = useState(null);   // FOLLOW-UP 3 (4d)
  const [recovery, setRecovery] = useState(null);       // FOLLOW-UP 4 (8g)
  const [recoveryRep, setRecoveryRep] = useState("");
  const [recoveryReps, setRecoveryReps] = useState([]);   // rep options, from the unfiltered load
  const [pnlSnapshot, setPnlSnapshot] = useState(null);
  const [pnlPeriod, setPnlPeriod] = useState("this_month");
  const [recvKpis, setRecvKpis] = useState(null);
  const canViewRecv = isAdmin || isFinance || isSalesMgr;

  const isAdmin_dup_removed = null; // Role checks moved to top of component
  const role = roles[0] || ""; // Keep for display purposes

  const dp = { date_from: dateFrom, date_to: dateTo }; // date params for all API calls

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [api.get("/dashboard/stats", { params: dp })];
      if (isAdmin || isSalesMgr || isFinance) {
        promises.push(api.get("/dashboard/charts/revenue-by-channel", { params: dp }));
        promises.push(api.get("/dashboard/charts/order-pipeline", { params: dp }));
        promises.push(api.get("/dashboard/charts/top-customers", { params: { ...dp, limit: 10 } }));
        promises.push(api.get("/dashboard/charts/receivables-ageing"));
        promises.push(api.get("/dashboard/queues/pending-approvals"));
      }
      if (isAdmin || isSalesMgr) {
        promises.push(api.get("/dashboard/charts/top-skus", { params: { ...dp, limit: 10 } }));
        promises.push(api.get("/dashboard/charts/team-performance", { params: dp }));
      }
      if (isAdmin || isFinance) { promises.push(api.get("/dashboard/queues/overdue-orders")); }
      if (isAdmin || isSalesMgr || isSalesExec) { promises.push(api.get("/dashboard/queues/inactive-customers")); }
      if (isAdmin || isWarehouse) { promises.push(api.get("/dashboard/queues/warehouse")); }
      if (isWhMgr || isAdmin) { promises.push(api.get("/dashboard/warehouse/staff-productivity")); promises.push(api.get("/dashboard/warehouse/expiry-alerts")); }
      if (isLogMgr || isAdmin) { promises.push(api.get("/dashboard/logistics/freight-cost-mtd")); promises.push(api.get("/dashboard/logistics/transporter-scorecard")); }

      const results = await Promise.allSettled(promises);
      let idx = 0;
      const get = () => { const r = results[idx++]; return r?.status === "fulfilled" ? r.value.data?.data : null; };

      setStats(get());
      if (isAdmin || isSalesMgr || isFinance) {
        setChannelData(get() || []);
        setPipelineData(get() || []);
        setTopCustomers(get() || []);
        setAgeingData(get() || []);
        setPendingQueue(get() || []);
      }
      if (isAdmin || isSalesMgr) {
        setTopSKUs(get() || []);

      // Load top SKUs by quantity separately + previous period for trend
      if (isAdmin || isSalesMgr) {
        api.get("/dashboard/charts/top-skus", { params: { ...dp, limit: 10, sort_by: "quantity" } }).then(r => setTopSKUsByQty(r.data?.data || [])).catch(err => console.error("API get /dashboard/charts/top-skus error:", err));
        // Previous period for trend comparison
        const days = Math.max(1, Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000));
        const prevEnd = new Date(new Date(dateFrom).getTime() - 86400000);
        const prevStart = new Date(prevEnd.getTime() - days * 86400000);
        api.get("/dashboard/charts/top-skus", { params: { date_from: prevStart.toISOString().slice(0,10), date_to: prevEnd.toISOString().slice(0,10), limit: 10 } }).then(r => setTopSKUsPrev(r.data?.data || [])).catch(err => console.error("API get /dashboard/charts/top-skus error:", err));
      }
        setTeamPerf(get() || []);
      }
      if (isAdmin || isFinance) { setOverdueOrders(get() || []); }
      if (isAdmin || isSalesMgr || isSalesExec) { setInactiveCustomers(get() || []); }
      if (isAdmin || isWarehouse) { setWarehouseQueue(get() || []); }
      if (isWhMgr || isAdmin) { setStaffProductivity(get() || []); setExpiryAlerts(get() || []); }
      if (isLogMgr || isAdmin) { setFreightCostMTD(get()); setTransporterScorecard(get() || []); }

      // SS Stock Widget — separate fetch (not in Promise.allSettled chain)
      if (isAdmin || isSalesMgr) {
        api.get("/ss-inventory/dashboard-widget").then(r => setSsStockWidget(r.data || [])).catch(err => console.error("API get /ss-inventory/dashboard-widget error:", err));
      }
      // Load channels for dispatch filter
      if (canViewDispatch && allChannels.length === 0) {
        api.get("/channels").then(r => setAllChannels(r.data || [])).catch(err => console.error("API get /channels error:", err));
      }

      // Dispatch Widget — separate fetch for permission-gated widget
      if (canViewDispatch) {
        api.get("/dashboard/dispatch-widget", { params: { ...dp, page: 1, limit: 20 } })
          .then(r => { setDispatchWidget(r.data || { data: [], total: 0, summary: {} }); setDispatchPage(1); })
          .catch(err => console.error("DashboardPage error:", err));
      }

      // Load personal SLA score for scored users
      if (!isAdmin && (isWarehouse || isLogistics || isFinance || isSalesMgr || isSalesExec)) {
        api.get("/reports/qcom-sla-performance/individual", { params: { target_name: user?.name || "", ...dp } })
          .then(r => setMySlaScore(r.data))
          .catch(() => setMySlaScore(null));
      }

      // Load logistics KPIs — respect dashboard period filter
      if (isAdmin || isLogistics || isFinance) {
        api.get("/dashboard/logistics-kpis", { params: { date_from: dateFrom || dp.date_from, date_to: dateTo || dp.date_to } })
          .then(r => setLogKpis(r.data))
          .catch(() => setLogKpis(null));
      }

      // FOLLOW-UP 4 (8g): the rep's own "recover before hard block" list
      api.get("/orders/overdue-recovery", { params: recoveryRep ? { sales_rep_id: recoveryRep } : {} })
        .then(r => {
          const d = r.data?.data || null;
          setRecovery(d);
          if (d && !recoveryRep) {
            const seen = {};
            [...(d.soft || []), ...(d.hard || [])].forEach(x => {
              if (x.sales_rep_id) seen[x.sales_rep_id] = x.sales_rep_name || x.sales_rep_id;
            });
            setRecoveryReps(Object.entries(seen).map(([id, name]) => ({ id, name })));
          }
        })
        .catch(() => setRecovery(null));

      // FOLLOW-UP 3 (4d): GRN recorded but the order was never marked Delivered
      if (isAdmin || isLogistics) {
        api.get("/orders/grn-not-delivered-count")
          .then(r => setGrnWaiting(r.data?.count ?? null))
          .catch(() => setGrnWaiting(null));
      }

      // Load PnL Snapshot (invoice-date based)
      if (isAdmin || isFinMgr || isFinance) {
        api.get("/dashboard/pnl-snapshot", { params: { period: pnlPeriod } })
          .then(r => setPnlSnapshot(r.data))
          .catch(() => setPnlSnapshot(null));
      }

      // Load Receivables KPIs
      if (isAdmin || isFinance || isSalesMgr) {
        api.get("/dashboard/receivables-kpis", { params: dp })
          .then(r => setRecvKpis(r.data))
          .catch(() => setRecvKpis(null));
      }

      setLastUpdated(new Date());
    } catch (err) { console.error("DashboardPage error:", err); } finally { setLoading(false); }
  }, [isAdmin, isSalesMgr, isSalesExec, isFinance, isFinMgr, isWarehouse, isWhMgr, isLogistics, isLogMgr, dateFrom, dateTo]);

  useEffect(() => { loadDashboard(); const iv = setInterval(loadDashboard, 300000); return () => clearInterval(iv); }, [loadDashboard]);

  const loadDispatchPage = async (pg) => {
    setDispatchLoading(true);
    try {
      const { data } = await api.get("/dashboard/dispatch-widget", { params: { date_from: dateFrom, date_to: dateTo, page: pg, limit: 20 } });
      setDispatchWidget(data || { data: [], total: 0, summary: {} });
      setDispatchPage(pg);
    } catch (err) { console.error("API get /dashboard/dispatch-widget error:", err); } finally { setDispatchLoading(false); }
  };

  const exportDispatch = async () => {
    try {
      const r = await api.get("/dashboard/dispatch-widget", { params: { date_from: dateFrom, date_to: dateTo, export: "excel" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "dispatched-orders.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const fmtDispatchDate = (dt) => {
    if (!dt || dt.length < 16) return dt || "—";
    const parts = dt.slice(0, 10).split("-");
    const time = dt.slice(11, 16);
    return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]} ${time}` : dt;
  };

  if (loading && !stats) return <div className="flex items-center justify-center py-20"><div className="text-sm text-zinc-400">Loading dashboard...</div></div>;

  const s = stats || {};

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Welcome back, {user?.name}. <span className="text-zinc-400">({role})</span></p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && <span className="text-[10px] text-zinc-400">Updated {Math.round((Date.now() - lastUpdated.getTime()) / 60000)}m ago</span>}
          <Button variant="outline" size="icon" onClick={loadDashboard} className="h-7 w-7 rounded-sm" title="Refresh"><RefreshCw size={12} /></Button>
          <Button variant="outline" className="h-7 rounded-sm text-[10px] px-2" onClick={async () => { try { await api.delete("/dashboard/preferences"); toast.success("Dashboard reset to default"); } catch (err) { console.error("Dashboard reset error:", err); } }} title="Reset layout">Reset</Button>
        </div>
      </div>

      {/* ═══ GLOBAL DATE RANGE PICKER ═══ */}
      <div className="flex items-center gap-2 mb-6 flex-wrap bg-zinc-50 border border-zinc-200 rounded-sm p-2" data-testid="date-range-picker">
        <Calendar size={14} className="text-zinc-400" />
        <span className="text-[10px] font-semibold uppercase text-zinc-400">Period:</span>
        {[
          { key: "today", label: "Today" },
          { key: "this_week", label: "This Week" },
          { key: "this_month", label: "This Month" },
          { key: "last_month", label: "Last Month" },
          { key: "this_quarter", label: "This Quarter" },
          { key: "this_year", label: "This Year" },
        ].map(p => (
          <Button key={p.key} variant={datePreset === p.key ? "default" : "outline"}
            className={`rounded-sm h-6 text-[10px] px-2 ${datePreset === p.key ? '' : ''}`}
            onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`}>{p.label}</Button>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setDatePreset("custom"); }} className="h-6 text-[10px] rounded-sm w-28 font-mono" />
          <span className="text-zinc-400 text-[10px]">to</span>
          <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setDatePreset("custom"); }} className="h-6 text-[10px] rounded-sm w-28 font-mono" />
        </div>
        <span className="text-[10px] text-zinc-400 ml-auto">{fmtDate(dateFrom)} to {fmtDate(dateTo)}</span>
      </div>

      {/* ═══ KPI TILES ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="kpi-grid">
        {(isAdmin || isSalesMgr || isSalesExec) && <KPI icon={IndianRupee} label={isSalesExec ? "My Revenue MTD" : isSalesMgr ? "Team Revenue MTD" : "Total Revenue MTD"} value={`${fmt(s.total_revenue)}`} change={s.revenue_change_pct} testId="kpi-revenue" onClick={() => navigate(`/orders?date_from=${dateFrom}&date_to=${dateTo}`)} />}
        {(isAdmin || isSalesMgr || isSalesExec || isFinance) && <KPI icon={ShoppingCart} label={isSalesExec ? "My Orders MTD" : "Orders MTD"} value={s.total_orders} sub={`${fmtR(s.booked_excl_gst)} booked (excl GST) · vs ${s.prev_orders || 0} last period`} testId="kpi-orders" onClick={() => navigate(`/orders?date_from=${dateFrom}&date_to=${dateTo}`)} />}
        {(isAdmin || isFinance) && <KPI icon={AlertTriangle} label="Outstanding Receivables" value={`${fmt(s.outstanding_receivables)}`} color={s.outstanding_receivables > 0 ? "text-amber-600" : ""} testId="kpi-outstanding" onClick={() => navigate("/orders?filter=outstanding")} />}
        {(isAdmin || isSalesMgr) && <KPI icon={Clock} label={isSalesMgr ? "Pending My Approval" : "Pending Approvals"} value={s.pending_approvals} color={s.pending_approvals > 0 ? "text-purple-600" : ""} testId="kpi-pending" onClick={() => navigate("/orders?filter=pending")} />}

        {/* Warehouse-specific KPIs (Fix R2-4) */}
        {(isAdmin || isWarehouse) && <KPI icon={Package} label="At Warehouse Now" value={s.orders_at_warehouse} testId="kpi-warehouse" onClick={() => navigate("/orders?status=At%20Warehouse")} />}
        {(isAdmin || isWarehouse) && <KPI icon={ClipboardCheck} label="Packed Today" value={s.packed_today || 0} testId="kpi-packed" onClick={() => navigate("/orders?status=Ready%20for%20Dispatch")} />}
        {(isAdmin || isWarehouse) && <KPI icon={Truck} label="Dispatched Today" value={s.dispatched_today} testId="kpi-dispatched" onClick={() => navigate("/orders?filter=dispatched_today")} />}

        {(isAdmin || isFinance) && <KPI icon={AlertTriangle} label="Overdue Payments" value={s.overdue_payments} color={s.overdue_payments > 0 ? "text-red-600" : ""} testId="kpi-overdue" onClick={() => navigate("/orders?filter=overdue")} />}
        {(isAdmin || isSalesMgr || isSalesExec) && <KPI icon={Users} label={isSalesExec ? "My Active Customers" : "Active Customers"} value={s.total_customers} sub={`${s.all_customers || s.total_customers} total`} testId="kpi-customers" onClick={() => navigate(`/orders?date_from=${dateFrom}&date_to=${dateTo}`)} />}
        {(isAdmin || isSalesMgr || isSalesExec) && <KPI icon={Users} label="Inactive 30+ Days" value={s.inactive_customers} color={s.inactive_customers > 0 ? "text-amber-600" : ""} testId="kpi-inactive" onClick={() => navigate("/orders?filter=inactive_customers")} />}
        {(isAdmin || isFinance) && <KPI icon={FileText} label="Ready to Close" value={s.orders_ready_to_close} testId="kpi-close" onClick={() => navigate("/orders?filter=ready_to_close")} />}

        {/* Logistics KPIs */}
        {isLogistics && <KPI icon={Truck} label="Awaiting Vehicle" value={s.orders_at_warehouse} testId="kpi-awaiting-vehicle" onClick={() => navigate("/orders?status=Ready%20for%20Dispatch")} />}
        {isLogistics && <KPI icon={Package} label="In Transit" value={s.dispatched_today} testId="kpi-in-transit" onClick={() => navigate("/orders?status=Dispatched")} />}

        {/* SLA Score Widget */}
        {mySlaScore && !isAdmin && (
          <button onClick={() => navigate("/reports/qcom-sla-performance")} className="kpi-tile text-left w-full" data-testid="kpi-sla-score">
            <p className="text-[10px] font-semibold tracking-[0.06em] uppercase text-zinc-400">My SLA Score</p>
            <p className={`font-heading text-2xl font-bold mt-1 ${mySlaScore.compliance >= 80 ? 'text-green-600' : mySlaScore.compliance >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{mySlaScore.total_points} pts</p>
            <p className="text-[10px] text-zinc-400 mt-0.5">{mySlaScore.compliance}% compliance · {mySlaScore.total_stages} stages</p>
          </button>
        )}

        {/* Logistics KPI Widgets */}

        {/* Receivables KPI Cards */}
        {canViewRecv && recvKpis && (
          <KPI icon={Wallet} label="Receivables" value={fmt(recvKpis.receivables)} sub={`${recvKpis.receivables_count} orders · Net outstanding`} color="text-amber-600" testId="kpi-receivables" onClick={() => navigate("/reports/receivables?tab=outstanding")} inverse changeLabel="vs last week" change={recvKpis.outstanding_wow?.available ? recvKpis.outstanding_wow.pct : undefined} />
        )}
        {canViewRecv && recvKpis && (
          <KPI icon={IndianRupee} label="Received" value={fmt(recvKpis.received)} sub={`${recvKpis.received_count} payments in this period${(recvKpis.received_on_rejected_cancelled?.amount || 0) > 0 ? ` · ${fmt(recvKpis.received_on_rejected_cancelled.amount)} on rejected / cancelled orders` : ""}`} color="text-green-600" testId="kpi-received" onClick={() => navigate("/reports/receivables?tab=paid")} />
        )}
        {canViewRecv && recvKpis && (
          <KPI icon={CreditCard} label="Settled by CN" value={fmt(recvKpis.cn_settled)} sub="Settled without cash" color="text-blue-600" testId="kpi-cn-settled" onClick={() => navigate("/reports/receivables")} />
        )}
        {canViewRecv && recvKpis && (
          <KPI icon={Minus} label="Deductions" value={fmt(recvKpis.deductions)} sub="TDS / claims / short" color="text-zinc-600" testId="kpi-deductions" onClick={() => navigate("/reports/receivables")} />
        )}
        {canViewRecv && recvKpis && (
          <KPI icon={AlertTriangle} label="Overdue" value={fmt(recvKpis.overdue)} sub={`${recvKpis.overdue_count} orders · Past due date`} color="text-red-600" testId="kpi-overdue-recv" onClick={() => navigate("/reports/receivables?tab=overdue")} inverse changeLabel="vs last week" change={recvKpis.overdue_wow?.available ? recvKpis.overdue_wow.pct : undefined} />
        )}

        {/* Logistics KPI Widgets */}
        {recovery && (recovery.soft.length > 0 || (recovery.hard_customers || []).length > 0) && (
          <div className="col-span-2 md:col-span-4 border border-amber-300 bg-amber-50/60 rounded-sm p-3" data-testid="recover-before-hard-block">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase text-amber-900">Recover before hard block</p>
              <p className="text-[10px] text-amber-800">{`₹${fmt(recovery.soft_total)}`} in grace · {`₹${fmt(recovery.hard_total)}`} already blocked</p>
            </div>
            <div className="overflow-auto max-h-64">
              <table className="w-full text-[11px]">
                <thead className="text-left text-amber-900">
                  <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-semibold">
                    <th>Customer</th><th>Order</th><th>Due date</th><th className="text-right">Days overdue</th>
                    <th className="text-right">Outstanding</th><th>Hard block from</th><th className="text-right">Days left</th>
                  </tr>
                </thead>
                <tbody>
                  {recovery.soft.map((r) => (
                    <tr key={r.order_id} className="border-t border-amber-200 [&>td]:py-1 [&>td]:pr-3"
                        data-testid={`recover-soft-${r.order_number}`}>
                      <td>
                        {r.customer_name}
                        {r.unlocked_until && <span className="block text-[10px] text-emerald-700" data-testid={`recover-unblocked-${r.order_number}`}>Unblocked by Admin until {fmtDate(r.unlocked_until)}</span>}
                      </td>
                      <td><a className="font-mono underline" href={`/orders/${r.order_id}`} onClick={(e) => { e.preventDefault(); navigate(`/orders/${r.order_id}`); }}>{r.order_number}</a></td>
                      <td className="font-mono">{fmtDate(r.due_date)}</td>
                      <td className="text-right font-mono">{r.days_overdue}</td>
                      <td className="text-right font-mono">{`₹${fmt(r.outstanding)}`}</td>
                      <td className="font-mono">{fmtDate(r.hard_block_date)}</td>
                      <td className="text-right font-mono font-semibold">{r.days_to_hard_block}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* PASS 2 (P6): one line per blocked customer, biggest first, top 10 only */}
            {(recovery.hard_customers || []).length > 0 && (
              <div className="mt-3" data-testid="recover-hard-block">
                <p className="text-[11px] text-red-700 font-semibold mb-1" data-testid="recover-hard-line">
                  Already hard blocked — {(recovery.hard_customers || []).length} customer(s) · {`₹${fmt(recovery.hard_total)}`} to recover before they can order again.
                </p>
                <table className="w-full text-[11px]">
                  <thead className="text-left text-red-800">
                    <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-semibold">
                      <th>Customer</th><th className="text-right">Orders</th>
                      <th className="text-right">Outstanding</th><th className="text-right">Oldest days overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recovery.hard_customers || []).slice(0, 10).map((c) => (
                      <tr key={c.customer_id || c.customer_name} className="border-t border-red-200 bg-red-50 text-red-800 [&>td]:py-1 [&>td]:pr-3"
                          data-testid={`recover-hard-customer-${c.customer_name}`}>
                        <td>{c.customer_name}</td>
                        <td className="text-right font-mono">{c.orders}</td>
                        <td className="text-right font-mono">{`₹${fmt(c.outstanding)}`}</td>
                        <td className="text-right font-mono">{c.oldest_days_overdue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(recovery.hard_customers || []).length > 10 && (
                  <p className="text-[10px] text-red-700 mt-1" data-testid="recover-hard-more">
                    + {(recovery.hard_customers || []).length - 10} more customers — see the Receivables report
                  </p>
                )}
              </div>
            )}
            {(recovery.sees_all || recovery.sees_team) && (
              <select value={recoveryRep} onChange={(e) => setRecoveryRep(e.target.value)}
                      className="mt-2 w-full md:w-80 h-7 text-[11px] border border-amber-300 rounded-sm px-2 bg-white"
                      data-testid="recover-rep-filter">
                <option value="">All sales reps</option>
                {recoveryReps.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
          </div>
        )}
        {grnWaiting > 0 && (isAdmin || isLogistics) && (
          <div className="kpi-tile cursor-pointer" data-testid="kpi-grn-not-delivered"
               onClick={() => navigate("/orders?filter=grn_not_delivered")}
               title="GRN or delivery date recorded, but the order is still not marked Delivered">
            <p className="text-[10px] font-semibold uppercase text-zinc-400">GRN in, not Delivered</p>
            <p className="font-heading text-2xl font-bold mt-1 text-amber-600" data-testid="kpi-grn-not-delivered-value">{grnWaiting}</p>
            <p className="text-[10px] text-zinc-400 mt-0.5">Mark them Delivered to start the credit clock</p>
          </div>
        )}
        {logKpis && (isAdmin || isLogistics || isFinance) && <>
          <div className="kpi-tile" data-testid="kpi-cost-per-kg" title={logKpis.cost_per_kg?.no_data ? "Needs actual freight data (upload via Logistics module) to show a value." : logKpis.cost_per_kg?.has_estimate_fallback ? "Based on estimated freight. Will update to actual once vendor invoices are uploaded." : ""}>
            <p className="text-[10px] font-semibold tracking-[0.06em] uppercase text-zinc-400">Logistics cost per kg<InfoDot text={WIDGET_INFO["kpi-cost-per-kg"]} testId="kpi-cost-per-kg-info" /></p>
            {logKpis.cost_per_kg?.no_data ? (
              <><p className="font-heading text-2xl font-bold mt-1 text-zinc-300">&mdash;</p><p className="text-[10px] text-zinc-400 mt-0.5">No data in selected period</p><span className="mt-1 inline-block px-1.5 py-0.5 text-[8px] font-bold rounded-sm bg-zinc-100 text-zinc-400">No data</span></>
            ) : (
              <><p className="font-heading text-2xl font-bold mt-1">{fmtR(logKpis.cost_per_kg.value)}/kg{logKpis.cost_per_kg.has_estimate_fallback ? ' (est.)' : ''}</p><p className="text-[10px] text-zinc-400 mt-0.5">{logKpis.cost_per_kg.weight_kg} kg · {logKpis.cost_per_kg.shipments} shipments</p></>
            )}
          </div>
          <div className="kpi-tile" data-testid="kpi-cost-pct" title={logKpis.cost_pct?.no_data ? "Needs freight data to calculate cost percentage." : logKpis.cost_pct?.has_estimate_fallback ? "Based on estimated freight. Will update to actual once vendor invoices are uploaded." : ""}>
            <p className="text-[10px] font-semibold tracking-[0.06em] uppercase text-zinc-400">Logistics cost %<InfoDot text={WIDGET_INFO["kpi-cost-pct"]} testId="kpi-cost-pct-info" /></p>
            {logKpis.cost_pct?.no_data ? (
              <><p className="font-heading text-2xl font-bold mt-1 text-zinc-300">&mdash;</p><p className="text-[10px] text-zinc-400 mt-0.5">No data in selected period</p><span className="mt-1 inline-block px-1.5 py-0.5 text-[8px] font-bold rounded-sm bg-zinc-100 text-zinc-400">No data</span></>
            ) : (
              <><p className={`font-heading text-2xl font-bold mt-1 ${logKpis.cost_pct.status === 'healthy' ? 'text-green-600' : logKpis.cost_pct.status === 'warning' ? 'text-amber-600' : 'text-red-600'}`}>{logKpis.cost_pct.value}%{logKpis.cost_pct.has_estimate_fallback ? ' (est.)' : ''}</p><p className="text-[10px] text-zinc-400 mt-0.5">Rs {fmtR(logKpis.cost_pct.freight)} on Rs {fmtR(logKpis.cost_pct.revenue ?? logKpis.cost_pct.invoice_value)} revenue (excl. GST)</p><span className={`mt-1 inline-block px-1.5 py-0.5 text-[8px] font-bold rounded-sm ${logKpis.cost_pct.status === 'healthy' ? 'bg-green-100 text-green-700' : logKpis.cost_pct.status === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{(logKpis.cost_pct.status || '').toUpperCase()}{logKpis.cost_pct.has_estimate_fallback ? ' (est.)' : ''}</span></>
            )}
          </div>
          <div className="kpi-tile" data-testid="kpi-accuracy" title={logKpis.accuracy?.no_data ? "Needs orders with BOTH estimated and actual freight to calculate accuracy. Upload vendor invoices first." : ""}>
            <p className="text-[10px] font-semibold tracking-[0.06em] uppercase text-zinc-400">Logistics estimate accuracy<InfoDot text={WIDGET_INFO["kpi-accuracy"]} testId="kpi-accuracy-info" /></p>
            {logKpis.accuracy?.no_data ? (
              <><p className="font-heading text-2xl font-bold mt-1 text-zinc-300">&mdash;</p><p className="text-[10px] text-zinc-400 mt-0.5">Needs both estimated + actual data</p><span className="mt-1 inline-block px-1.5 py-0.5 text-[8px] font-bold rounded-sm bg-zinc-100 text-zinc-400">No data</span></>
            ) : (
              <><p className={`font-heading text-2xl font-bold mt-1 ${logKpis.accuracy.status === 'on_track' ? 'text-green-600' : 'text-amber-600'}`}>{logKpis.accuracy.value}%</p><p className="text-[10px] text-zinc-400 mt-0.5">Est {fmtR(logKpis.accuracy.estimated)} vs Act {fmtR(logKpis.accuracy.actual)}</p><span className={`mt-1 inline-block px-1.5 py-0.5 text-[8px] font-bold rounded-sm ${logKpis.accuracy.status === 'on_track' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{logKpis.accuracy.status === 'on_track' ? 'ON TRACK' : 'OFF TRACK'}</span></>
            )}
          </div>
        </>}
      </div>

      {/* ═══ CHARTS ROW ═══ */}
      {(isAdmin || isSalesMgr || isFinance) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <WidgetCard title="Revenue by Channel (MTD)">
            {channelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={channelData}><XAxis dataKey="channel" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip contentStyle={{ fontSize: 11 }} /><Bar dataKey="revenue" fill="#09090B" radius={[2, 2, 0, 0]} cursor="pointer" onClick={(d) => navigate("/orders")} /></BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-zinc-400 text-center py-8">No channel data yet</p>}
          </WidgetCard>

          <WidgetCard title="Order Pipeline">
            {pipelineData.length > 0 ? (
              <div className="space-y-1.5">
                {pipelineData.map((d, i) => (
                  <button key={d.stage} onClick={() => navigate(`/orders?status=${encodeURIComponent(d.stage)}`)} className="w-full flex items-center gap-2 hover:bg-zinc-50 rounded-sm px-1 py-0.5 group transition-colors">
                    <span className="text-[10px] text-zinc-500 w-28 text-right truncate">{d.stage.replace("Pending with ", "")}</span>
                    <div className="flex-1 bg-zinc-100 rounded-full h-4 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, d.count * 10)}%`, backgroundColor: PIPELINE_COLORS[d.stage] || '#94A3B8' }} />
                    </div>
                    <span className="font-mono text-xs font-bold w-8 text-right">{d.count}</span>
                    <ArrowRight size={10} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            ) : <p className="text-xs text-zinc-400 text-center py-8">No orders in pipeline</p>}
          </WidgetCard>
        </div>
      )}

      {/* ═══ DISPATCHED ORDERS WIDGET ═══ */}
      {canViewDispatch && (
        <div className="mb-6" data-testid="dispatch-widget">
          <div className="border border-zinc-200">
            <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-semibold text-zinc-900">Dispatched Orders — Actual Dispatch Dates<InfoDot text={WIDGET_INFO["Dispatched Orders — Actual Dispatch Dates"]} testId="widget-info-dispatch" /></h3>
                <span className="px-1.5 py-0.5 text-[9px] font-bold bg-teal-100 text-teal-800 rounded-sm" data-testid="dispatch-count-badge">{dispatchWidget.total || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                {canExportDispatch && (dispatchWidget.data || []).length > 0 && (
                  <Button variant="outline" className="h-6 text-[10px] px-2 rounded-sm" onClick={exportDispatch} data-testid="dispatch-export-btn">
                    <FileText size={10} /> Export Excel
                  </Button>
                )}
              </div>
            </div>
            <div className="p-4">
              {/* CR-2: Search + Channel Filter */}
              {(dispatchWidget.data || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <div className="relative flex-1 min-w-[180px] max-w-[280px]">
                    <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input value={dispatchSearch} onChange={e => setDispatchSearch(e.target.value)} placeholder="Search order or customer..." className="pl-8 h-7 text-[10px] rounded-sm" data-testid="dispatch-search" />
                    {dispatchSearch && <button onClick={() => setDispatchSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400"><X size={10} /></button>}
                  </div>
                  <MultiSelectDropdown label="Trade Format" options={getValues("channel_types")} selected={dispatchTFFilter} onChange={v => { setDispatchTFFilter(v); setDispatchChannelFilter([]); }} className="h-7" />
                  <MultiSelectDropdown label="Sales Channel" options={allChannels.filter(c => c.status === "active" && (dispatchTFFilter.length === 0 || dispatchTFFilter.includes(c.type))).map(c => ({ value: c.name, label: c.name }))} selected={dispatchChannelFilter} onChange={setDispatchChannelFilter} />
                  {(dispatchSearch || dispatchChannelFilter.length > 0 || dispatchTFFilter.length > 0) && <Button variant="ghost" onClick={() => { setDispatchSearch(""); setDispatchChannelFilter([]); setDispatchTFFilter([]); }} className="h-7 text-[10px] px-2 text-red-600"><X size={10} /> Clear</Button>}
                </div>
              )}
              {(() => {
                const allRows = dispatchWidget.data || [];
                const filteredRows = allRows.filter(row => {
                  if (dispatchSearch) {
                    const s = dispatchSearch.toLowerCase();
                    if (!row.order_number?.toLowerCase().includes(s) && !row.customer_name?.toLowerCase().includes(s)) return false;
                  }
                  if (dispatchChannelFilter.length > 0 && !dispatchChannelFilter.includes(row.channel_name)) return false;
                  return true;
                });
                const showingFiltered = dispatchSearch || dispatchChannelFilter.length > 0 || dispatchTFFilter.length > 0;
                return (<>
                  {showingFiltered && <p className="text-[10px] text-zinc-400 mb-2">{filteredRows.length} of {allRows.length} orders</p>}
              {dispatchLoading ? <p className="text-xs text-zinc-400 text-center py-6">Loading...</p> :
              filteredRows.length === 0 ? <p className="text-xs text-zinc-400 text-center py-6">{showingFiltered ? "No matching orders" : "No dispatched orders in selected period"}</p> : (
                <>
                  <div className="overflow-x-auto">
                    <table className="data-table w-full" data-testid="dispatch-table">
                      <thead><tr><th>Order</th><th>Customer</th><th>Sales Channel</th><th>SKUs</th><th className="num">Boxes</th><th>Dispatch Date</th><th>Dispatched By</th>{(canEditDispatch || canDeleteDispatch) && <th className="w-20">Actions</th>}</tr></thead>
                      <tbody>
                        {filteredRows.map(row => (
                          <tr key={row._id} data-testid={`dispatch-row-${row._id}`}>
                            <td><button onClick={() => navigate(`/orders/${row._id}`)} className="font-mono text-xs text-blue-600 hover:underline">{row.order_number}</button></td>
                            <td className="text-xs">{row.customer_name}</td>
                            <td className="text-xs">{row.channel_name}</td>
                            <td className="text-xs max-w-[200px] truncate" title={row.skus}>{row.skus}</td>
                            <td className="num font-mono text-xs">{row.total_boxes}</td>
                            <td className="text-[10px] font-mono text-zinc-600">{fmtDispatchDate(row.actual_dispatch_at)}</td>
                            <td className="text-xs">{row.dispatched_by}</td>
                            {(canEditDispatch || canDeleteDispatch) && (
                              <td>
                                <div className="flex gap-1">
                                  {canEditDispatch && <button onClick={() => navigate(`/orders/${row._id}`)} className="text-[10px] text-blue-600 hover:underline" data-testid={`dispatch-view-${row._id}`}>View</button>}
                                  {canDeleteDispatch && (
                                    <button onClick={async () => {
                                      const reason = window.prompt("Reason for removing this dispatch record (mandatory):");
                                      if (!reason || reason.trim().length < 5) { if (reason !== null) toast.error("Reason must be at least 5 characters"); return; }
                                      try { await api.delete(`/dashboard/dispatch-widget/${row._id}`, { data: { reason: reason.trim() } }); toast.success("Record removed"); loadDispatchPage(dispatchPage); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                                    }} className="text-[10px] text-red-600 hover:underline" data-testid={`dispatch-delete-${row._id}`}>Remove</button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Summary line */}
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <p className="text-[10px] text-zinc-500" data-testid="dispatch-summary">
                      {dispatchWidget.summary?.total_orders || 0} orders dispatched — {dispatchWidget.summary?.total_boxes || 0} total boxes — in selected period
                    </p>
                    {/* Pagination */}
                    {(dispatchWidget.total || 0) > 20 && (
                      <div className="flex items-center gap-1">
                        <Button variant="outline" disabled={dispatchPage <= 1} onClick={() => loadDispatchPage(dispatchPage - 1)} className="h-6 text-[10px] px-2 rounded-sm">Prev</Button>
                        <span className="text-[10px] text-zinc-500">Page {dispatchPage} of {Math.ceil((dispatchWidget.total || 0) / 20)}</span>
                        <Button variant="outline" disabled={dispatchPage >= Math.ceil((dispatchWidget.total || 0) / 20)} onClick={() => loadDispatchPage(dispatchPage + 1)} className="h-6 text-[10px] px-2 rounded-sm">Next</Button>
                      </div>
                    )}
                  </div>
                </>
              )}
              </>); })()}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SECOND ROW — Tables ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Top Customers */}
        {(isAdmin || isSalesMgr || isFinance) && topCustomers.length > 0 && (
          <WidgetCard title="Top Customers by Revenue">
            <div className="space-y-1">
              {topCustomers.slice(0, 8).map((c, i) => (
                <button key={i} onClick={() => navigate(`/orders?customer_id=${c.customer_id || ''}`)} className="w-full flex items-center justify-between py-1 hover:bg-zinc-50 rounded-sm px-1 group transition-colors">
                  <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-zinc-400 w-4">{i + 1}</span><span className="text-xs text-zinc-900 truncate max-w-[140px]">{c.customer}</span></div>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold">{fmtR(c.revenue)}</span><span className="text-[10px] text-zinc-400">{c.orders} orders</span><ArrowRight size={10} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                </button>
              ))}
            </div>
          </WidgetCard>
        )}

        {/* Receivables Ageing */}
        {(isAdmin || isFinance) && (
          <WidgetCard title="Receivables Ageing">
            {ageingData.some(d => d.amount > 0) ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={ageingData}><XAxis dataKey="bucket" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip contentStyle={{ fontSize: 11 }} /><Bar dataKey="amount" radius={[2, 2, 0, 0]}>{ageingData.map((d, i) => <Cell key={i} fill={d.bucket === "90+" ? "#DC2626" : d.bucket === "61-90" ? "#F59E0B" : "#09090B"} />)}</Bar></BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-zinc-400 text-center py-8">No outstanding receivables</p>}
          </WidgetCard>
        )}

        {/* Pending Approvals Queue — click navigates to order */}
        {(isAdmin || isSalesMgr || isFinance) && (
          <WidgetCard info={WIDGET_INFO["Pending Approvals"]} title={<div className="flex items-center justify-between w-full"><span>Pending Approvals</span><button onClick={(e) => { e.stopPropagation(); navigate("/orders?status=Pending%20with%20Manager"); }} className="text-[9px] text-blue-600 hover:underline">View All →</button></div>}>
            {pendingQueue.length > 0 ? (
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {pendingQueue.slice(0, 8).map(o => (
                  <button key={o._id} onClick={() => navigate(`/orders/${o._id}`)} className="w-full flex items-center justify-between py-1 hover:bg-zinc-50 rounded-sm px-1 text-left group">
                    <div><span className="font-mono text-[10px] text-zinc-500">{o.order_number}</span><span className="text-xs text-zinc-700 ml-2">{o.customer}</span></div>
                    <div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold">{fmtR(o.value)}</span><span className="px-1.5 py-0.5 text-[8px] font-semibold rounded-sm bg-purple-100 text-purple-800">{o.status?.replace("Pending with ", "")}</span><ArrowRight size={10} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                  </button>
                ))}
              </div>
            ) : <p className="text-xs text-zinc-400 text-center py-6">No pending approvals</p>}
          </WidgetCard>
        )}
      </div>

      {/* ═══ THIRD ROW ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Enhanced Top SKUs Card Widget (CR-4) */}
        {(isAdmin || isSalesMgr) && (topSKUs.length > 0 || topSKUsByQty.length > 0) && (
          <div className="lg:col-span-2 border border-zinc-200 rounded-sm" data-testid="top-skus-widget">
            <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-zinc-900">Top SKUs<InfoDot text={WIDGET_INFO["Top SKUs"]} testId="widget-info-top-skus" /></h3>
              <div className="flex items-center gap-2">
                <button onClick={() => { api.get("/dashboard/charts/top-skus/export", { params: { date_from: dateFrom, date_to: dateTo, sort_by: skuView }, responseType: "blob" }).then(r => { const url = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = url; a.download = "Top_SKUs.xlsx"; a.click(); toast.success("Downloaded"); }).catch(() => toast.error("No data")); }} className="text-[9px] text-zinc-400 hover:text-blue-600" title="Download Excel"><Download size={12} /></button>
                <div className="flex bg-zinc-100 rounded-sm p-0.5">
                  <button onClick={() => setSkuView("revenue")} className={`px-2 py-0.5 text-[10px] rounded-sm transition-colors ${skuView === "revenue" ? 'bg-white shadow-sm text-zinc-900 font-semibold' : 'text-zinc-500'}`}>By Revenue</button>
                  <button onClick={() => setSkuView("quantity")} className={`px-2 py-0.5 text-[10px] rounded-sm transition-colors ${skuView === "quantity" ? 'bg-white shadow-sm text-zinc-900 font-semibold' : 'text-zinc-500'}`}>By Quantity</button>
                </div>
              </div>
            </div>
            <div className="p-3">
              {(() => {
                const skuList = skuView === "revenue" ? topSKUs.slice(0, 10) : topSKUsByQty.slice(0, 10);
                const totalRev = skuList.reduce((s, sk) => s + (sk.revenue || 0), 0);
                const prevMap = {};
                topSKUsPrev.forEach(s => { prevMap[s.sku_id || s.sku] = s; });
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {skuList.map((s, i) => {
                      const prev = prevMap[s.sku_id || s.sku];
                      const prevVal = skuView === "revenue" ? (prev?.revenue || 0) : (prev?.units || 0);
                      const curVal = skuView === "revenue" ? (s.revenue || 0) : (s.units || 0);
                      const trend = prev ? (curVal > prevVal ? "up" : curVal < prevVal ? "down" : "flat") : "flat";
                      const revPct = totalRev > 0 ? Math.round((s.revenue || 0) / totalRev * 100) : 0;
                      const fmtLakh = (n) => n >= 100000 ? `${(n/100000).toFixed(1)}L` : n >= 1000 ? `${(n/1000).toFixed(1)}K` : fmt(n);
                      return (
                        <div key={i} className="border border-zinc-100 rounded-sm p-2.5 hover:border-zinc-300 transition-colors cursor-pointer" data-testid={`sku-card-${i}`} onClick={() => navigate(`/orders?search=${encodeURIComponent(s.sku)}`)}>
                          <div className="flex items-start justify-between mb-1">
                            <span className="px-1 py-0.5 text-[8px] font-mono font-bold bg-zinc-100 text-zinc-500 rounded-sm">#{i + 1}</span>
                          </div>
                          <p className="text-[11px] font-medium text-zinc-900 truncate mb-1" title={s.sku}>{s.sku}</p>
                          <p className="font-mono text-sm font-bold text-zinc-800 mb-0.5">{fmtLakh(s.revenue || 0)}</p>
                          <div className="w-full bg-zinc-100 rounded-full h-1 mb-1.5"><div className="bg-blue-500 h-1 rounded-full" style={{ width: `${revPct}%` }} /></div>
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] text-zinc-400">{s.units || 0} units</span>
                            <span className={`text-[9px] font-semibold px-1 py-0.5 rounded-sm ${trend === "up" ? "bg-green-50 text-green-700" : trend === "down" ? "bg-red-50 text-red-700" : "bg-zinc-50 text-zinc-400"}`}>
                              {trend === "up" ? "\u2191" : trend === "down" ? "\u2193" : "\u2014"} {prev ? `${Math.abs(Math.round((curVal - prevVal) / (prevVal || 1) * 100))}%` : ""}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Team Performance */}
        {(isAdmin || isSalesMgr) && teamPerf.length > 0 && (
          <WidgetCard info={WIDGET_INFO["Team Performance"]} title={<div className="flex items-center justify-between w-full"><span>Team Performance</span><button onClick={(e) => { e.stopPropagation(); api.get("/dashboard/charts/team-performance/export", { params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" }).then(r => { const url = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = url; a.download = "Team_Performance.xlsx"; a.click(); toast.success("Downloaded"); }).catch(() => toast.error("No data")); }} className="text-zinc-400 hover:text-blue-600" title="Download Excel"><Download size={12} /></button></div>}>
            <div className="space-y-1">
              <div className="flex items-center justify-end gap-3 pb-1 text-[9px] uppercase tracking-wide text-zinc-400" data-testid="team-perf-legend">
                <span>Booked (incl GST, by order date)</span><span>Invoiced revenue (excl GST)</span>
              </div>
              {teamPerf.map((t, i) => (
                <button key={i} onClick={() => navigate(`/orders?search=${encodeURIComponent(t.name)}`)} className="w-full flex items-center justify-between py-1 px-1 hover:bg-zinc-50 rounded-sm group transition-colors">
                  <span className="text-xs text-zinc-900">{t.name}</span>
                  <div className="flex items-center gap-3"><span className="text-[10px] text-zinc-500">{t.orders} orders</span><span className="text-[10px] text-zinc-500">booked {fmtR(t.booked_incl_gst ?? t.revenue)}</span><span className="font-mono text-xs font-semibold" title="Invoiced revenue (excl GST)">{fmtR(t.invoiced_revenue_excl_gst ?? 0)}</span><ArrowRight size={10} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                </button>
              ))}
            </div>
          </WidgetCard>
        )}

        {/* Overdue Orders */}
        {(isAdmin || isFinance) && overdueOrders.length > 0 && (
          <WidgetCard title="Overdue Payments">
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {overdueOrders.slice(0, 8).map(o => (
                <button key={o._id} onClick={() => navigate(`/orders/${o._id}`)} className="w-full flex items-center justify-between py-1 hover:bg-zinc-50 rounded-sm px-1 text-left group">
                  <div><span className="font-mono text-[10px]">{o.order_number}</span><span className="text-xs text-zinc-700 ml-2">{o.customer}</span></div>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold text-red-600">{fmtR(o.outstanding)}</span><span className="text-[10px] text-red-500">{o.days_overdue}d</span><ArrowRight size={10} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                </button>
              ))}
            </div>
          </WidgetCard>
        )}

        {/* Inactive Customers — click navigates, download exports */}
        {(isAdmin || isSalesMgr || isSalesExec) && inactiveCustomers.length > 0 && (
          <WidgetCard info={WIDGET_INFO["Follow-Up — Inactive Customers"]} title={<div className="flex items-center justify-between w-full"><span>Follow-Up — Inactive Customers</span><button onClick={(e) => { e.stopPropagation(); api.get("/reports/generate/inactive_customers", { params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" }).then(r => { const url = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = url; a.download = "Inactive_Customers.xlsx"; a.click(); toast.success("Downloaded"); }).catch(() => toast.error("Export failed")); }} className="text-zinc-400 hover:text-blue-600" title="Download Excel"><Download size={12} /></button></div>}>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {inactiveCustomers.slice(0, 8).map((c, i) => (
                <button key={i} onClick={() => navigate("/customers")} className="w-full flex items-center justify-between py-1 px-1 hover:bg-zinc-50 rounded-sm group transition-colors text-left">
                  <div><span className="text-xs text-zinc-900">{c.name}</span>{c.phone && <span className="text-[10px] text-zinc-400 ml-2">{c.phone}</span>}</div>
                  <div className="flex items-center gap-2"><span className="text-[10px] text-amber-600 font-semibold">{c.days_inactive}d inactive</span><ArrowRight size={10} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                </button>
              ))}
            </div>
          </WidgetCard>
        )}

        {/* Warehouse Queue — click opens order, header links to filtered view */}
        {(isAdmin || isWarehouse) && warehouseQueue.length > 0 && (
          <WidgetCard info={WIDGET_INFO["Warehouse Queue"]} title={<div className="flex items-center justify-between w-full"><span>Warehouse Queue</span><button onClick={(e) => { e.stopPropagation(); navigate("/orders?status=At%20Warehouse"); }} className="text-[9px] text-blue-600 hover:underline">View All →</button></div>}>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {warehouseQueue.slice(0, 8).map(o => (
                <button key={o._id} onClick={() => navigate(`/orders/${o._id}`)} className="w-full flex items-center justify-between py-1 hover:bg-zinc-50 rounded-sm px-1 text-left group">
                  <div><span className="font-mono text-[10px]">{o.order_number}</span><span className="text-xs text-zinc-700 ml-2">{o.customer}</span></div>
                  <div className="flex items-center gap-2"><span className="px-1.5 py-0.5 text-[8px] font-semibold rounded-sm bg-orange-100 text-orange-800">{o.status}</span><ArrowRight size={10} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                </button>
              ))}
            </div>
          </WidgetCard>
        )}

        {/* PnL Snapshot (Invoice Date Basis) */}
        {(isFinMgr || isAdmin || isFinance) && pnlSnapshot && (
          <WidgetCard info={WIDGET_INFO["PnL Snapshot (Invoice Date Basis)"]} title={<div className="flex items-center justify-between w-full"><span>PnL Snapshot (Invoice Date Basis)</span><div className="flex items-center gap-1"><button onClick={(e) => { e.stopPropagation(); navigate("/reports/pnl-audit"); }} className="text-[9px] text-blue-600 hover:underline">View Report →</button><select value={pnlPeriod} onChange={e => { setPnlPeriod(e.target.value); api.get("/dashboard/pnl-snapshot", { params: { period: e.target.value } }).then(r => setPnlSnapshot(r.data)).catch(err => console.error("API get /dashboard/pnl-snapshot error:", err)); }} className="text-[10px] border border-zinc-200 rounded-sm px-1 h-6 text-zinc-600"><option value="this_month">This Month</option><option value="last_month">Last Month</option><option value="this_quarter">This Quarter</option></select></div></div>}>            <div className="space-y-1.5">
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Revenue (excl. GST)</span><span className="font-mono font-semibold">{fmtR(pnlSnapshot.revenue_excl_gst)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">COGS</span><span className="font-mono text-red-600">-{fmtR(pnlSnapshot.total_cogs)}</span></div>
              <div className="flex justify-between text-xs border-t border-zinc-100 pt-1"><span className="text-zinc-700 font-medium">Gross Margin</span><span className={`font-mono font-semibold ${pnlSnapshot.gross_margin >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmtR(pnlSnapshot.gross_margin)} ({pnlSnapshot.gross_margin_pct}%)</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Logistics Cost</span><span className="font-mono text-red-600">-{fmtR(pnlSnapshot.logistics_cost)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">RTO Deductions</span><span className="font-mono text-red-600">-{fmtR(pnlSnapshot.rto_deductions)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Credit Notes</span><span className="font-mono text-red-600">-{fmtR(pnlSnapshot.credit_notes)}</span></div>
              <div className="border-t border-zinc-200 my-1" />
              <div className="flex justify-between text-sm"><span className="text-zinc-800 font-bold">Net Margin</span><span className={`font-mono font-bold text-lg ${pnlSnapshot.net_margin >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmtR(pnlSnapshot.net_margin)} ({pnlSnapshot.net_margin_pct}%)</span></div>
              <p className="text-[9px] text-zinc-400 mt-1">Based on {pnlSnapshot.total_orders} invoiced orders ({fmtDate(pnlSnapshot.period?.from)} to {fmtDate(pnlSnapshot.period?.to)})</p>
              {pnlSnapshot.pending_invoice_count > 0 && <p className="text-[9px] text-amber-600">Pending Invoice: {pnlSnapshot.pending_invoice_count} orders worth {fmtR(pnlSnapshot.pending_invoice_value)}</p>}
            </div>
          </WidgetCard>
        )}
      </div>

      {/* ═══ WAREHOUSE MANAGER WIDGETS (Deepak only) ═══ */}
      {isWhMgr && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 mt-6">
          {/* Staff Productivity */}
          <WidgetCard title="Staff Productivity (Today)">
            {staffProductivity.length > 0 ? (
              <div className="border border-zinc-200 overflow-hidden">
                <table className="data-table w-full">
                  <thead><tr><th>Warehouse Executive</th><th className="num">Processed</th><th className="num">Packed</th></tr></thead>
                  <tbody>
                    {staffProductivity.map((sp, i) => (
                      <tr key={i}><td className="text-xs font-medium text-zinc-900">{sp.name}</td><td className="num font-mono">{sp.processed_today}</td><td className="num font-mono">{sp.packed_today}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-xs text-zinc-400 text-center py-4">No warehouse executives found</p>}
          </WidgetCard>

          {/* Expiry Alerts */}
          <WidgetCard title="Expiry Alerts (30 Days)">
            {expiryAlerts.length > 0 ? (
              <div className="space-y-1 max-h-[220px] overflow-y-auto">
                {expiryAlerts.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between py-1.5 px-2 rounded-sm ${a.severity === 'red' ? 'bg-red-50' : 'bg-amber-50'}`}>
                    <div>
                      <span className="text-xs font-medium text-zinc-900">{a.sku_name}</span>
                      <span className="text-[10px] text-zinc-500 ml-2">Batch: {a.batch_number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-600">Qty: {a.quantity}</span>
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${a.severity === 'red' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                        {a.days_to_expiry}d
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-zinc-400 text-center py-4">No batches expiring within 30 days</p>}
          </WidgetCard>
        </div>
      )}

      {/* ═══ LOGISTICS MANAGER WIDGETS (Rohan only) ═══ */}
      {isLogMgr && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 mt-6">
          {/* Freight Cost MTD */}
          <WidgetCard title="Freight Cost MTD">
            {freightCostMTD ? (
              <div className="space-y-2">
                <div className="flex items-end gap-2">
                  <span className="font-heading text-3xl font-bold text-zinc-900">{fmtR(freightCostMTD.current_month)}</span>
                  <span className={`text-xs font-semibold mb-1 ${freightCostMTD.change === 'higher' ? 'text-amber-600' : freightCostMTD.change === 'lower' ? 'text-green-600' : 'text-zinc-400'}`}>
                    {freightCostMTD.change === 'higher' ? '↑ Higher' : freightCostMTD.change === 'lower' ? '↓ Lower' : '— Same'} vs last month
                  </span>
                </div>
                <div className="flex gap-4 text-xs text-zinc-500">
                  <span>Last month: {fmtR(freightCostMTD.last_month)}</span>
                  <span>Shipments: {freightCostMTD.shipments}</span>
                </div>
              </div>
            ) : <p className="text-xs text-zinc-400 text-center py-4">No freight data</p>}
          </WidgetCard>

          {/* Transporter Scorecard */}
          <WidgetCard title="Transporter Scorecard (MTD)">
            {transporterScorecard.length > 0 ? (
              <div className="border border-zinc-200 overflow-hidden">
                <table className="data-table w-full">
                  <thead><tr><th>Transporter</th><th className="num">Shipments</th><th className="num">On-Time %</th><th className="num">Avg Freight</th></tr></thead>
                  <tbody>
                    {transporterScorecard.map((t, i) => (
                      <tr key={i}>
                        <td className="text-xs font-medium text-zinc-900">{t.transporter}</td>
                        <td className="num font-mono">{t.shipments}</td>
                        <td className={`num font-mono font-semibold ${t.on_time_pct >= 90 ? 'text-green-600' : t.on_time_pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{t.on_time_pct}%</td>
                        <td className="num font-mono">{fmtR(t.avg_freight)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-xs text-zinc-400 text-center py-4">No transporter data this month</p>}
          </WidgetCard>
        </div>
      )}

      {/* SS Stock Status Widget — Admin + Sales Manager */}
      {(isAdmin || isSalesMgr) && ssStockWidget.length > 0 && (
        <WidgetCard title="SS Stock Status" className="mt-4">
          <div className="border border-zinc-200 overflow-hidden" data-testid="ss-stock-widget">
            <table className="data-table w-full">
              <thead><tr><th>SS Name</th><th className="num">SKUs Tracked</th><th className="num">In Stock</th><th className="num">Low Stock</th><th className="num">Critical</th><th>Next Expiry</th></tr></thead>
              <tbody>
                {ssStockWidget.map((ss, i) => (
                  <tr key={i} className="cursor-pointer hover:bg-zinc-50" onClick={() => navigate(`/ss-inventory?ss=${ss.ss_customer_id}`)} data-testid={`ss-widget-row-${i}`}>
                    <td className="text-xs font-medium text-zinc-900">{ss.ss_name}</td>
                    <td className="num font-mono">{ss.total_skus}</td>
                    <td className="num font-mono text-green-600">{ss.in_stock}</td>
                    <td className="num font-mono text-amber-600">{ss.low_stock}</td>
                    <td className={`num font-mono ${ss.critical > 0 ? 'text-red-600 font-semibold' : ''}`}>{ss.critical}</td>
                    <td className="text-xs text-zinc-500">{ss.next_expiry ? fmtDate(ss.next_expiry) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </WidgetCard>
      )}
    </div>
  );
}
